import React, { useEffect, useState } from 'react';
import { Empty, Segmented } from 'antd';
import { formatters } from 'services';
import { ASSET_CLASSES } from 'state';
import type { Studio2Player } from '../../live/types';
import MiniChart from '../MiniChart';
import AssetPositioningTile, {
  type SelectedInstrument,
} from './AssetPositioningTile';
import { pct, BAND_COLORS } from '../../helpers';
import type { RelativizeDate } from '../../helpers';

const pctFmt = formatters.percent1Digit;
const moneyFmt = (v: number) => formatters.million(v);
const colorsForSign = (vals: number[]) =>
  vals.map(v => (v >= 0 ? BAND_COLORS[0] : BAND_COLORS[3]));

// Each Top-N chart always renders this many category slots so the per-bar width
// stays constant no matter how many instruments qualify — surplus slots hold
// null (no bar), leaving the bars left-aligned with blank space to the right.
const TOP_N = 5;

// Arrange an already-descending-sorted list into a symmetric "bell": the largest
// item in the MIDDLE, tapering to the smallest at the top & bottom edges. Mirrors
// the backend bell_sort in compute_concentrations (fills the slot left of centre
// first, then right) so the Concentration tile reads as a bell curve.
const bellOrder = <T,>(desc: T[]): T[] => {
  const n = desc.length;
  // Empty in → empty out. Without this guard the unconditional `out[mid] = ...`
  // below writes out[0] = undefined for n === 0, returning [undefined] (length 1)
  // and crashing downstream maps for unutilized players with no concentrations.
  if (n === 0) return [];
  const out = new Array<T>(n);
  const mid = Math.floor(n / 2);
  let left = mid - 1;
  let right = mid + 1;
  let i = 0;
  out[mid] = desc[i++];
  while (i < n) {
    if (left >= 0) {
      out[left--] = desc[i++];
      if (i >= n) break;
    }
    if (right < n) out[right++] = desc[i++];
  }
  return out;
};

/** No-data placeholder for a tile whose analysis flow hasn't produced rows yet
 *  (e.g. concentrations / per-instrument PnL before their compute has run). */
const NotComputed: React.FC = () => (
  <div
    style={{
      flex: '1 1 auto',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: 0,
    }}
  >
    <Empty
      image={Empty.PRESENTED_IMAGE_SIMPLE}
      description={<span className="s2-muted">Not computed yet.</span>}
    />
  </div>
);

const DriversPanel: React.FC<{
  player: Studio2Player;
  relativizeDate?: RelativizeDate;
}> = ({ player, relativizeDate }) => {
  // "Return by geography" tile: currently sourced from the individual report
  // (legacy — being phased out). Should move to a pnl-distribution-derived
  // geography breakdown in the future, like the asset-class/per-instrument data.
  const geo = Object.entries(
    player.report.returns_breakdown.return_per_geography,
  );
  const acr = Object.entries(
    player.report.returns_breakdown.return_per_asset_class,
  );
  // Show the eight largest concentrations arranged as a BELL CURVE (largest in the
  // middle, tapering to the edges). We can't just slice player.concentration:
  // the backend stores the FULL set in bell order (ConcentrationBellCurve.sort_index),
  // so a raw slice(0, 8) would grab the bell's left wing and miss the largest
  // names. So take the top 8 by exposure, then bell-arrange those 8. Both the
  // exposure and PnL series follow this order since they map over the same rows.
  const conc = bellOrder(
    [...player.concentration]
      .sort(
        (a, b) =>
          b.reporting_value_period_weighted - a.reporting_value_period_weighted,
      )
      .slice(0, 8),
  );

  const dist = player.pnl_distribution;
  // Track the selected asset class by CODE (not list index) so the selection
  // persists across player changes — players list their classes in different
  // orders. Falls back to the first class when a player lacks the selected one.
  const [selectedClass, setSelectedClass] = useState<number | null>(null);
  const active = dist.find(d => d.asset_class === selectedClass) ?? dist[0];
  // Split the selected class's instruments into its biggest winners and biggest
  // losers: the 5 largest positive PnLs (descending) and the 5 largest losses
  // (most-negative first). reporting_pnl is signed and non-nullable, so strict
  // >0 / <0 cleanly drops flat positions from both charts; the name tie-break
  // keeps ordering deterministic when two instruments share a PnL.
  const byInstrument = active?.per_instrument_distributions ?? [];
  const winners = byInstrument
    .filter(i => i.reporting_pnl > 0)
    .sort(
      (a, b) =>
        b.reporting_pnl - a.reporting_pnl || a.name.localeCompare(b.name),
    )
    .slice(0, TOP_N);
  const losers = byInstrument
    .filter(i => i.reporting_pnl < 0)
    .sort(
      (a, b) =>
        a.reporting_pnl - b.reporting_pnl || a.name.localeCompare(b.name),
    )
    .slice(0, TOP_N);

  // The "Positioning vs market" tile drills into ONE instrument. Default to the
  // active class's top profit driver; clicking any winner/loser bar overrides it.
  // Reset when the player or asset class changes so the default tracks the view.
  const [pickedInstrument, setPickedInstrument] =
    useState<SelectedInstrument | null>(null);
  useEffect(() => {
    setPickedInstrument(null);
  }, [player.id, active?.asset_class]);
  const topRow = winners[0] ?? losers[0] ?? null;
  const selectedInstrument: SelectedInstrument | null =
    pickedInstrument ??
    (topRow
      ? {
          financial_instrument_id: topRow.financial_instrument_id,
          underlying_asset_id: topRow.underlying_asset_id ?? null,
          name: topRow.name,
        }
      : null);

  // Clicking a "Return by asset class" bar drives the PnL distribution below.
  // Match by asset-class name (robust for real data, not just index-aligned).
  const selectedName = active ? ASSET_CLASSES[active.asset_class] : null;
  const handleAssetClassClick = (i: number) => {
    const name = acr[i]?.[0];
    if (name == null) return;
    const found = dist.find(d => ASSET_CLASSES[d.asset_class] === name);
    if (found) setSelectedClass(found.asset_class);
  };

  return (
    <div className="studio2__drivers">
      <div className="studio2__card">
        <div className="studio2__card-head">
          <span className="studio2__card-title">Return by geography</span>
        </div>
        <div className="studio2__card-body studio2__card-body--pad0">
          <MiniChart
            type="bar"
            horizontal
            series={[{ name: 'Return', data: geo.map(([, v]) => v) }]}
            categories={geo.map(([k]) => k)}
            colors={colorsForSign(geo.map(([, v]) => v))}
            distributed
            valueFormatter={pctFmt}
          />
        </div>
      </div>

      <div className="studio2__card">
        <div className="studio2__card-head">
          <span className="studio2__card-title">Return by asset class</span>
          <span
            className="s2-muted"
            style={{ fontWeight: 500, fontSize: 11, textTransform: 'none' }}
          >
            click a bar to inspect ↓
          </span>
        </div>
        <div className="studio2__card-body studio2__card-body--pad0">
          <MiniChart
            type="bar"
            horizontal
            series={[{ name: 'Return', data: acr.map(([, v]) => v) }]}
            categories={acr.map(([k]) => k)}
            colors={acr.map(([name, v]) =>
              name === selectedName
                ? '#0f5fd6'
                : v >= 0
                  ? BAND_COLORS[0]
                  : BAND_COLORS[3],
            )}
            distributed
            valueFormatter={pctFmt}
            onBarClick={handleAssetClassClick}
            style={{ cursor: 'pointer' }}
          />
        </div>
      </div>

      <div className="studio2__card">
        <div className="studio2__card-head">
          <span className="studio2__card-title">
            Concentration (period-weighted) vs. PnL
          </span>
        </div>
        <div className="studio2__card-body studio2__card-body--pad0">
          {/*
            Two grouped bars per instrument on one shared axis:
              • Avg exposure — period-weighted AVERAGE reporting value (always ≥ 0)
              • Total PnL    — CUMULATIVE realized + unrealized session PnL (signed)
            The two metrics intentionally differ in time-semantics (average vs
            total); they share one axis so each instrument gets a paired bar, and
            because PnL is signed its bar extends LEFT of zero on a loss. PnL is
            null until the session is recomputed with the PnL-enabled task → 0.
          */}
          {conc.length === 0 ? (
            <NotComputed />
          ) : (
            <MiniChart
              type="bar"
              horizontal
              series={[
                {
                  name: 'Avg exposure',
                  data: conc.map(c => c.reporting_value_period_weighted),
                },
                {
                  name: 'Total PnL',
                  data: conc.map(c => c.reporting_pnl ?? 0),
                },
              ]}
              categories={conc.map(c => c.asset_name)}
              colors={['#0f5fd6', '#d48806']}
              valueFormatter={moneyFmt}
            />
          )}
        </div>
      </div>

      <div className="studio2__card studio2__card--half-left">
        <div className="studio2__card-head">
          <span className="studio2__card-title">
            Per-instrument PnL{' '}
            {active && (
              <span
                className="s2-muted"
                style={{ fontWeight: 500, textTransform: 'none' }}
              >
                — {ASSET_CLASSES[active.asset_class]} · class return{' '}
                {pct(active.asset_class_return)}
              </span>
            )}
          </span>
          {dist.length > 0 && (
            <Segmented
              size="small"
              value={active?.asset_class}
              onChange={v => setSelectedClass(v as number)}
              options={dist.map(d => ({
                label: ASSET_CLASSES[d.asset_class],
                value: d.asset_class,
              }))}
            />
          )}
        </div>
        {/*
          The selected class's five biggest winners (left) and five biggest
          losers (right), side by side. Two independent MiniChart instances split
          the card body via a 2-column grid; each fills its half. colorsForSign
          makes the winners all-green and the losers all-red by construction.
        */}
        {dist.length === 0 ? (
          <div className="studio2__card-body studio2__card-body--pad0">
            <NotComputed />
          </div>
        ) : (
          <div
            className="studio2__card-body studio2__card-body--pad0"
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}
          >
            {(
              [
                { label: 'Top 5 profit', rows: winners, kind: 'win' },
                { label: 'Top 5 loss', rows: losers, kind: 'loss' },
              ] as const
            ).map(({ label, rows, kind }, col) => {
              // Pad to TOP_N slots so the per-bar width matches the full-five case;
              // the surplus slots carry null (no bar) and stay blank on the right
              // while the real bars fill in from the left.
              const pad = TOP_N - rows.length;
              const data: (number | null)[] = [
                ...rows.map(i => i.reporting_pnl),
                ...Array(pad).fill(null),
              ];
              const categories: string[] = [
                ...rows.map(i => i.name),
                ...Array(pad).fill(''),
              ];
              const colors: string[] = [
                ...colorsForSign(rows.map(i => i.reporting_pnl)),
                ...Array(pad).fill('transparent'),
              ];
              return (
                <div
                  key={kind}
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    minHeight: 0,
                    borderLeft:
                      col === 1 ? '1px solid var(--s2-border-soft)' : undefined,
                  }}
                >
                  <span
                    className="studio2__card-title"
                    style={{ flex: '0 0 auto', padding: '8px 12px' }}
                  >
                    {label}
                  </span>
                  <div style={{ flex: '1 1 auto', minHeight: 0 }}>
                    <MiniChart
                      key={`${player.id}-${active?.asset_class ?? 'none'}-${kind}`}
                      type="bar"
                      series={[{ name: 'PnL', data }]}
                      categories={categories}
                      colors={colors}
                      distributed
                      valueFormatter={moneyFmt}
                      onBarClick={idx => {
                        const r = rows[idx];
                        if (!r) return;
                        setPickedInstrument({
                          financial_instrument_id: r.financial_instrument_id,
                          underlying_asset_id: r.underlying_asset_id ?? null,
                          name: r.name,
                        });
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <AssetPositioningTile
        playerId={player.id}
        selected={selectedInstrument}
        relativizeDate={relativizeDate}
      />
    </div>
  );
};

export default DriversPanel;
